import * as React from "react";
import { graphql, compose } from "react-apollo";
import gql from "graphql-tag";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Typography,
  TextField,
} from "@material-ui/core";

import { User } from "../../userTypes";
import handleMutationError from "app/utils/handleMutationError";

// react-apollo 1.x has no MutationFunction type, so the injected mutation is
// typed by hand with the variables reset_manual_verification_request takes.
type ResetManualVerificationRequestMutation = (options: {
  variables: { externalReference: string; comment: string };
}) => Promise<unknown>;

type Props = {
  user: User;
  onClose: () => void;
  resetManualVerificationRequest?: ResetManualVerificationRequestMutation;
};

type State = {
  fetchingData: boolean;
  commentInput: string;
};

// Confirms resetting an errored manual verification request to pending, after
// which review can be initiated as normal.
class ManualVerificationResetConfirmationModal extends React.Component<
  Props,
  State
> {
  state = { fetchingData: false, commentInput: "" };

  resetManualVerificationRequest = async () => {
    try {
      this.setState({ fetchingData: true });
      await this.props.resetManualVerificationRequest({
        variables: {
          externalReference: this.props.user.external_reference,
          comment: this.state.commentInput.trim(),
        },
      });
      window.location.reload();
    } catch (error) {
      this.setState({ fetchingData: false });
      handleMutationError(error);
    }
  };

  buttonHasRequiredInputs = (): boolean => {
    return this.state.commentInput.trim().length > 0;
  };

  render() {
    const request = this.props.user.manual_verification_request;

    return (
      <Dialog open={true} onClose={() => this.props.onClose()}>
        <DialogTitle>
          <DialogContentText>
            Confirm resetting the errored manual verification request of user:
          </DialogContentText>
        </DialogTitle>
        <DialogContent>
          <Typography style={{ whiteSpace: "pre-wrap" }}>
            Email: <strong>{this.props.user.email}</strong>
          </Typography>
          <Typography style={{ whiteSpace: "pre-wrap" }}>
            ID: <strong>{this.props.user.identity_number}</strong>
          </Typography>
          <Typography style={{ whiteSpace: "pre-wrap" }}>
            Error source: <strong>{request?.error_source || "none"}</strong>
          </Typography>
          <Typography style={{ whiteSpace: "pre-wrap" }}>
            Error reason: <strong>{request?.error_reason || "none"}</strong>
          </Typography>
          <Typography style={{ marginTop: "20px" }}>
            The request will be set back to pending so that manual verification
            can be initiated again. Please provide a reason for the reset below:
          </Typography>
          <div>
            <TextField
              label="Comment"
              style={{ width: "100%" }}
              value={this.state.commentInput}
              onChange={(event) =>
                this.setState({ commentInput: event.target.value })
              }
              multiline
              margin="normal"
            />
          </div>
        </DialogContent>
        <DialogActions>
          <Button color="secondary" onClick={() => this.props.onClose()}>
            Cancel
          </Button>
          <Button
            color="primary"
            onClick={() => {
              this.resetManualVerificationRequest();
            }}
            disabled={
              !this.buttonHasRequiredInputs() || this.state.fetchingData
            }
          >
            {this.state.fetchingData ? "Resetting" : "Confirm"}
          </Button>
        </DialogActions>
      </Dialog>
    );
  }
}

const resetManualVerificationRequest = graphql(
  gql`
    mutation resetManualVerificationRequest(
      $externalReference: String!
      $comment: String!
    ) {
      reset_manual_verification_request(
        external_reference: $externalReference
        comment: $comment
      )
    }
  `,
  {
    name: "resetManualVerificationRequest",
  }
);

export default compose<any>(resetManualVerificationRequest)(
  ManualVerificationResetConfirmationModal
);
